import { useMemo, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { MemberAvatar } from '@entities/group';
import { useSession } from '@entities/session';
import { CheckboxTick, TickCheckIcon, WalletIcon, X } from '@ui/icons';
import { cn, currencySymbol, formatMoney, fromMinorUnitFormatted, type Money } from '@shared/lib';
import { toast } from '@shared/lib/toast';
import { t } from '@shared/i18n';
import { equalSplitMinor } from '../model/split';
import type { SplitMember } from './SplitModal';

/** One payer's resolved contribution, emitted on Save. `paidAmountMinor` is minor units. */
export interface PaidByResultEntry {
  userId: string;
  type: 'user' | 'wallet';
  paidAmountMinor: number;
  splitEnabled: boolean;
  /** The paid-by METHOD chosen in the chained popup ('Percentage'/'Shares'/'Amount'/'Equal') and
   *  its raw per-payer value — carried onto the wire payment legs (mobile parity); absent for the
   *  plain equal division (single payer / wallet / no chained method picked). */
  splitType?: string;
  percentage?: number;
  shares?: number;
  amount?: number;
}

export interface PaidByModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  members: SplitMember[];
  totalMinor: number;
  currency: string;
  /** Group wallet balance in minor units; null when the group has no wallet. */
  walletBalanceMinor: number | null;
  /** Group wallet ACCOUNT id — the `paid_by` id used for a wallet payer (not the current user's id).
   *  '' falls back to the current user's id (legacy behaviour). */
  walletId?: string | undefined;
  /** Current payer(s) to pre-check when the multi-select checkboxes are shown (wallet → its row). */
  initialPayers?:
    | readonly { readonly userId: string; readonly type: 'user' | 'wallet' }[]
    | undefined;
  /** Called with the selected member payers (total split equally) when the user confirms. */
  onSave: (entries: PaidByResultEntry[]) => void;
  /** Original transaction amount in minor units (before conversion to group native currency). */
  transactionTotalMinor?: number | undefined;
  /** Original transaction currency code (e.g. 'USD'). */
  transactionCurrency?: string | undefined;
  /** The bill repeats — a repeating expense can't be paid from the wallet (mobile §16.1): the
   *  wallet row stays visible but can't be picked; tapping it explains why. */
  walletUnavailableForRecurring?: boolean | undefined;
}

const WALLET_ID = '__wallet__';

/** Square purple checkbox matching the Figma. */
function PayerCheck({ checked, disabled = false }: { checked: boolean; disabled?: boolean }) {
  return (
    <span
      aria-hidden="true"
      className={cn(
        'flex size-6 shrink-0 items-center justify-center rounded-md transition-colors',
        checked ? 'bg-violet text-white' : 'bg-[#E1E1E1]',
        disabled && 'opacity-40',
      )}
    >
      {checked ? <CheckboxTick className="size-3" /> : null}
    </span>
  );
}

/** Body — remounts each open so the mode resets to single-select (no checkboxes). */
function PaidByBody({
  members,
  totalMinor,
  currency,
  walletBalanceMinor,
  walletId,
  initialPayers,
  walletBlocked,
  onOpenChange,
  onSave,
}: {
  members: SplitMember[];
  totalMinor: number;
  currency: string;
  walletBalanceMinor: number | null;
  walletId?: string;
  initialPayers?:
    | readonly { readonly userId: string; readonly type: 'user' | 'wallet' }[]
    | undefined;
  /** Repeating bill — the wallet can't be picked (see `walletUnavailableForRecurring`). */
  walletBlocked: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (entries: PaidByResultEntry[]) => void;
}) {
  const p = t.newBill.paidByModal;
  const sym = currencySymbol(currency);
  const { data: user } = useSession();
  const hasWallet = walletBalanceMinor !== null;
  const walletSelectable = hasWallet && walletBalanceMinor > 0;

  const walletIsSufficient = hasWallet && walletBalanceMinor >= totalMinor;

  const initialSelected = () => {
    const set = new Set<string>();
    (initialPayers ?? []).forEach((payer) => {
      // A stale wallet payer on a repeating bill is never pre-checked either.
      if (payer.type === 'wallet' && (!walletSelectable || walletBlocked)) return;
      set.add(payer.type === 'wallet' ? WALLET_ID : payer.userId);
    });
    if (set.size === 0) {
      const youMember = members.find((m) => m.isYou || (user?.id && m.id === user.id));
      if (youMember) {
        set.add(youMember.id);
      }
    }
    return set;
  };

  const initialSet = initialSelected();
  const initialMulti =
    (initialPayers?.length ?? 0) > 1 || (initialSet.has(WALLET_ID) && !walletIsSufficient);

  const [multiMode, setMultiMode] = useState(initialMulti);
  const [selected, setSelected] = useState<Set<string>>(initialSet);

  const toggle = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const walletSelected = selected.has(WALLET_ID);
  const checkedMemberCount = members.filter((m) => selected.has(m.id)).length;
  const walletCheckDisabled =
    !walletSelectable || (!walletSelected && walletIsSufficient && checkedMemberCount > 0);
  const memberCheckDisabled = walletSelected && walletIsSufficient;
  const fallbackWalletUserId = walletId && walletId.length > 0 ? walletId : user?.id ?? '';

  const sortedMembers = useMemo(() => {
    return [...members].sort((a, b) => {
      const aIsYou = Boolean(a.isYou || (user?.id && a.id === user.id));
      const bIsYou = Boolean(b.isYou || (user?.id && b.id === user.id));
      if (aIsYou && !bIsYou) return -1;
      if (!aIsYou && bIsYou) return 1;
      return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
    });
  }, [members, user]);

  const emit = (entries: PaidByResultEntry[]) => {
    onSave(entries);
    onOpenChange(false);
  };

  const selectSingleWallet = () => {
    emit([
      {
        userId: fallbackWalletUserId,
        type: 'wallet',
        paidAmountMinor: Math.min(walletBalanceMinor ?? 0, totalMinor),
        splitEnabled: true,
      },
    ]);
  };

  const selectSingleMember = (memberId: string) => {
    emit([
      {
        userId: memberId,
        type: 'user',
        paidAmountMinor: totalMinor,
        splitEnabled: true,
      },
    ]);
  };

  const handleSaveMulti = () => {
    if (selected.size === 0) {
      toast.error('Please select at least one payer');
      return;
    }

    const hasWalletSelected = selected.has(WALLET_ID);
    const memberIds = Array.from(selected).filter((id) => id !== WALLET_ID);

    const walletCapacityMinor = hasWalletSelected
      ? Math.min(walletBalanceMinor ?? 0, totalMinor)
      : 0;

    if (hasWalletSelected && walletCapacityMinor < totalMinor && memberIds.length === 0) {
      toast.error(p.selectMemberForRemaining);
      return;
    }

    const membersRemainingTotalMinor = Math.max(0, totalMinor - walletCapacityMinor);
    const memberShares =
      memberIds.length > 0 ? equalSplitMinor(membersRemainingTotalMinor, memberIds) : {};

    const entries: PaidByResultEntry[] = [];
    if (hasWalletSelected) {
      entries.push({
        userId: fallbackWalletUserId,
        type: 'wallet',
        paidAmountMinor: walletCapacityMinor,
        splitEnabled: true,
      });
    }
    memberIds.forEach((id) => {
      entries.push({
        userId: id,
        type: 'user',
        paidAmountMinor: memberShares[id] ?? 0,
        splitEnabled: true,
      });
    });
    emit(entries);
  };

  return (
    <div className="min-h-0 flex-1 overflow-y-auto">
      {hasWallet ? (
        <button
          type="button"
          role={multiMode ? 'checkbox' : 'button'}
          aria-checked={multiMode ? walletSelected : undefined}
          aria-current={!multiMode && walletSelected ? true : undefined}
          // A blocked (repeating-bill) wallet stays CLICKABLE so the tap can explain itself.
          disabled={!walletBlocked && (!walletSelectable || (multiMode && walletCheckDisabled))}
          aria-disabled={walletBlocked || undefined}
          onClick={() => {
            if (walletBlocked) {
              toast.error(p.walletRecurringToast);
              return;
            }
            if (multiMode) {
              if (!walletCheckDisabled) toggle(WALLET_ID);
            } else {
              if (walletSelectable) {
                if (!walletIsSufficient) {
                  setMultiMode(true);
                  if (!selected.has(WALLET_ID)) toggle(WALLET_ID);
                } else {
                  selectSingleWallet();
                }
              }
            }
          }}
          className={cn(
            'flex w-full items-center gap-3 border-b border-divider px-6 py-4 text-left select-none outline-none focus-visible:ring-2 focus-visible:ring-ring transition-colors',
            walletSelectable &&
              (!multiMode || !walletCheckDisabled) &&
              'cursor-pointer hover:bg-black/2',
            (!walletSelectable || (multiMode && walletCheckDisabled)) &&
              'opacity-40 cursor-not-allowed hover:bg-transparent',
            walletBlocked && 'opacity-60 cursor-not-allowed hover:bg-transparent',
          )}
        >
          {multiMode ? (
            <PayerCheck
              checked={walletSelected}
              disabled={walletCheckDisabled || !walletSelectable}
            />
          ) : null}
          <span className="flex size-10 shrink-0 items-center justify-center rounded-full bg-accent-soft text-accent">
            <WalletIcon className="size-5" aria-hidden />
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-base font-semibold text-fg">{p.virtualWallet}</p>
            <p className="text-sm text-fg">
              {p.currentBalance} {formatMoney(walletBalanceMinor as Money, currency)}
            </p>
            {walletBlocked ? (
              <p className="mt-0.5 text-xs italic text-muted">{p.walletRecurringHint}</p>
            ) : null}
            {!walletIsSufficient && walletSelected ? (
              <p className="mt-0.5 text-sm font-medium text-danger">
                {p.lowBalance}{' '}
                <span className="font-bold">
                  {sym}
                  {fromMinorUnitFormatted(Math.max(0, totalMinor - walletBalanceMinor))}
                </span>
              </p>
            ) : null}
            {walletIsSufficient && walletSelected ? (
              <p className="mt-0.5 text-sm font-medium text-positive">
                {p.fullPaid} {sym}
                {fromMinorUnitFormatted(totalMinor)}
              </p>
            ) : null}
          </div>
          {!multiMode && walletSelected ? (
            <TickCheckIcon className="size-5 shrink-0 text-positive" aria-hidden />
          ) : null}
        </button>
      ) : null}

      <div className="flex flex-col pt-5 relative before:content-[''] before:absolute before:w-full before:h-5 before:bg-gradient-to-b before:from-[#D7D7ED]/30 before:to-[#F4F4FF]/0 before:top-0">
        <ul className="px-3">
          {sortedMembers.map((member, i) => {
            const isSelected = selected.has(member.id);
            const isSelf = Boolean(member.isYou || (user?.id && member.id === user.id));
            const isRowDisabled = multiMode && memberCheckDisabled;
            // Phone first; a member with no phone on file (e.g. the signed-in user, who may have
            // signed up with only an email) falls back to their email instead of showing nothing.
            const contact =
              member.phone && member.phone !== member.name ? member.phone : member.email;
            return (
              <li
                key={member.id}
                className={cn(i !== sortedMembers.length - 1 && 'border-b border-[#E7E7E7]')}
              >
                <button
                  type="button"
                  role={multiMode ? 'checkbox' : 'button'}
                  aria-checked={multiMode ? isSelected : undefined}
                  aria-current={!multiMode && isSelected ? true : undefined}
                  disabled={isRowDisabled}
                  onClick={() => {
                    if (multiMode) {
                      if (!memberCheckDisabled) toggle(member.id);
                    } else {
                      selectSingleMember(member.id);
                    }
                  }}
                  className={cn(
                    'flex w-full items-center gap-3 p-3 text-left select-none outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-lg transition-colors',
                    !isRowDisabled && 'cursor-pointer hover:bg-surface',
                    isRowDisabled && 'opacity-40 cursor-not-allowed hover:bg-transparent',
                  )}
                >
                  {multiMode ? (
                    <PayerCheck checked={isSelected} disabled={memberCheckDisabled} />
                  ) : null}
                  <MemberAvatar
                    name={member.name === member.phone ? '' : member.name}
                    phone={member.phone}
                    avatarUrl={member.avatarUrl}
                    size="md"
                    className="shrink-0"
                  />
                  <div className="min-w-0 flex-1">
                    <p className={cn('truncate text-base', isSelf ? 'text-info' : 'text-fg')}>
                      {member.name}
                    </p>
                    {contact ? <p className="truncate text-sm text-fg">{contact}</p> : null}
                  </div>
                  {!multiMode && isSelected ? (
                    <TickCheckIcon className="size-5 shrink-0 text-positive" aria-hidden />
                  ) : null}
                </button>
              </li>
            );
          })}
        </ul>
        <div className="p-5">
          <button
            type="button"
            onClick={multiMode ? handleSaveMulti : () => setMultiMode(true)}
            className="h-14 w-full cursor-pointer rounded-lg bg-accent-soft text-base font-medium text-[#454545] hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            {multiMode ? t.newBill.save : p.multiple}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * "Paid by" selection modal (Figma) — pick the virtual wallet and/or members who paid the bill,
 * surfacing an insufficient-balance hint when the wallet can't cover the total.
 */
export function PaidByModal({
  open,
  onOpenChange,
  members,
  totalMinor,
  currency,
  walletBalanceMinor,
  walletId,
  initialPayers,
  onSave,
  transactionTotalMinor,
  transactionCurrency,
  walletUnavailableForRecurring = false,
}: PaidByModalProps) {
  const p = t.newBill.paidByModal;
  const hasWallet = walletBalanceMinor !== null;
  const showTransactionAmount =
    hasWallet &&
    Boolean(transactionCurrency) &&
    transactionCurrency !== currency &&
    Boolean(transactionTotalMinor && transactionTotalMinor > 0);

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-60 bg-black/50" />
        <Dialog.Content
          aria-describedby={undefined}
          className="fixed left-1/2 top-1/2 z-60 flex max-h-[92vh] w-[calc(100vw-2rem)] max-w-[640px] -translate-x-1/2 -translate-y-1/2 flex-col overflow-hidden rounded-xl bg-surface-raised shadow-overlay focus:outline-none"
        >
          <div className="flex items-center gap-4 border-b border-border bg-header-bar px-5 py-2">
            <Dialog.Title className="flex-1 text-lg font-semibold text-fg-strong">
              {p.title} {currencySymbol(currency)} {fromMinorUnitFormatted(totalMinor)} {p.from}
              {showTransactionAmount && transactionCurrency && transactionTotalMinor ? (
                <span className="ml-1.5 text-xs font-normal text-danger">
                  ({p.transactionAmount} {currencySymbol(transactionCurrency)}
                  {''}
                  {fromMinorUnitFormatted(transactionTotalMinor)})
                </span>
              ) : null}
            </Dialog.Title>
            <Dialog.Close
              aria-label={t.newBill.close}
              className="flex size-10 shrink-0 cursor-pointer items-center justify-center rounded-full bg-border-soft text-fg hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <X className="size-4" />
            </Dialog.Close>
          </div>

          <PaidByBody
            members={members}
            totalMinor={totalMinor}
            currency={currency}
            walletBalanceMinor={walletBalanceMinor}
            {...(walletId ? { walletId } : {})}
            initialPayers={initialPayers}
            walletBlocked={walletUnavailableForRecurring}
            onOpenChange={onOpenChange}
            onSave={onSave}
          />
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
